import { useEffect, useState } from 'react';
import {
  Alert, Button, Card, DatePicker, Form,
  Input, InputNumber, Modal, Space, Tag, Tooltip, Typography, Upload,
} from 'antd';
import {
  CheckCircleOutlined, CheckOutlined, ClockCircleOutlined, CloseCircleOutlined,
  DeleteOutlined, FileTextOutlined,
  InfoCircleOutlined, SendOutlined, SyncOutlined, UploadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import AppButton from '../AppButton';
import '../../styles/timesheet.css';

const { Text, Title } = Typography;

// ── Status colour map ──────────────────────────────────────────────────────
const STATUS_COLOR = {
  draft:         'default',
  logged:        'blue',
  submitted:     'processing',
  approved:      'success',
  rejected:      'error',
  resubmitted:   'purple',
  save_draft:    'default',
  pending:       'warning',
  not_submitted: 'default',
};

// Status badge label + icon for the summary panel header
function StatusBadgeTag({ status }) {
  if (!status || status === 'draft' || status === 'not_submitted') return null;
  const map = {
    submitted:   { icon: <CheckOutlined />,        color: 'blue',   label: 'Submitted' },
    approved:    { icon: <CheckCircleOutlined />,   color: 'green',  label: 'Approved' },
    rejected:    { icon: <CloseCircleOutlined />,   color: 'red',    label: 'Rejected' },
    resubmitted: { icon: <SyncOutlined />,          color: 'purple', label: 'Re-Submitted' },
    save_draft:  { icon: null,                      color: 'default',label: 'Draft' },
  };
  const cfg = map[status];
  if (!cfg) return <Tag color={STATUS_COLOR[status] ?? 'default'}>{status}</Tag>;
  return (
    <Tag color={cfg.color} icon={cfg.icon} style={{ borderRadius: 20, fontSize: 12, padding: '2px 10px' }}>
      {cfg.label}
    </Tag>
  );
}

// ── Log type CSS class ─────────────────────────────────────────────────────
const LOG_TYPE_CLASS = {
  working: 'timesheet-day-cell--working',
  leave:   'timesheet-day-cell--leave',
  holiday: 'timesheet-day-cell--holiday',
  weekoff: 'timesheet-day-cell--weekoff',
};


// ── Helpers ────────────────────────────────────────────────────────────────
function numberValue(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function formatHours(v) {
  const n = numberValue(v);
  return n === 0 ? '0 hrs' : `${n.toFixed(1).replace(/\.0$/, '')} hrs`;
}

function formatHoursShort(v) {
  const n = numberValue(v);
  return n === 0 ? '0h' : `${n.toFixed(1).replace(/\.0$/, '')}h`;
}

// Hour-split colours. Must stay in step with TIMESHEET_LEGEND on the consumer side.
const SPLIT_COLORS = {
  ST:    '#D0E6FF',
  OT:    '#AAFEBCA6',
  leave: '#FFECBA',
};

// Convert a desired AREA fraction into a gradient stop position.
//
// On a diagonal (135deg) gradient across a square, a stop at 25% does NOT colour 25%
// of the visible area — the corner is a triangle whose area grows as t². The area below
// a stop at axis-fraction t is 2t² (for t <= 0.5), so invert that to place the stop.
// Without this, 6h+2h would paint a stop at 75% but *look* like ~88% — the ratio the
// user is meant to read off the cell would be wrong.
function areaToStop(area, angle) {
  const a = Math.min(Math.max(area, 0), 1);
  // Straight (vertical/horizontal) gradients are already area-linear.
  if (!String(angle).startsWith('135') && !String(angle).startsWith('45')) return a;
  return a <= 0.5
    ? Math.sqrt(a / 2)
    : 1 - Math.sqrt((1 - a) / 2);
}

/**
 * buildDayGradient — background for a day cell, proportional to the hours logged.
 *
 * Returns null when the day has 0 or 1 hour types (a flat class colour is enough).
 * Exported so consumers can unit-test the ratio maths.
 */
export function buildDayGradient(day = {}, { angle = '135deg' } = {}) {
  const segments = [
    ['ST',    numberValue(day?.workingHours?.ST)],
    ['OT',    numberValue(day?.workingHours?.OT)],
    ['leave', numberValue(day?.leave?.CL) + numberValue(day?.leave?.SL)],
  ].filter(([, hours]) => hours > 0);

  const total = segments.reduce((sum, [, hours]) => sum + hours, 0);
  if (segments.length < 2 || total <= 0) return null; // single type -> solid class colour

  const stops = [];
  let cumulative = 0;
  segments.forEach(([key, hours], index) => {
    const from = areaToStop(cumulative / total, angle) * 100;
    cumulative += hours;
    const to = index === segments.length - 1
      ? 100
      : areaToStop(cumulative / total, angle) * 100;
    stops.push(`${SPLIT_COLORS[key]} ${from.toFixed(2)}% ${to.toFixed(2)}%`);
  });

  return `linear-gradient(${angle}, ${stops.join(', ')})`;
}

function getDayClass(day = {}, { isUnloggedHighlight = false, selected = false, selectable = false, reviewMode = false } = {}) {
  const st = numberValue(day?.workingHours?.ST);
  const ot = numberValue(day?.workingHours?.OT);
  const leave = numberValue(day?.leave?.CL) + numberValue(day?.leave?.SL);
  const isMixed = [st, ot, leave].filter((h) => h > 0).length > 1;

  // A mixed day gets its background from the inline computed gradient, so no log-type
  // colour class is applied (it would paint a flat colour over the split).
  const logType = day?.logType ?? (day?.isHoliday ? 'holiday' : day?.isWeekOff ? 'weekoff' : null);
  const status = day?.status;

  return [
    'timesheet-day-cell',
    !isMixed && logType ? (LOG_TYPE_CLASS[logType] ?? '') : '',
    day?.isFuture              ? 'timesheet-day-cell--future'             : '',
    status === 'rejected'      ? 'timesheet-day-cell--rejected'           : '',
    status === 'approved'      ? 'timesheet-day-cell--approved'           : '',
    selected                   ? 'timesheet-day-cell--selected'           : '',
    isUnloggedHighlight        ? 'timesheet-day-cell--unlogged-highlight' : '',
    selectable                 ? 'timesheet-day-cell--selectable'         : '',
    reviewMode                 ? 'timesheet-day-cell--review'             : '',
  ].filter(Boolean).join(' ');
}

// ── TimesheetDayCell ───────────────────────────────────────────────────────
/**
 * A single day in the timesheet calendar.
 *
 * Three modes, all opt-in so existing consumers are unaffected:
 *  - default        : resource logs hours (click -> DayLogModal via onSelect)
 *  - selectable     : quick-log selection (click -> onToggleSelect, NOT onSelect)
 *  - reviewMode     : approver sees ✓/✗ controls on hover
 *
 * The hours background is an inline gradient computed from the day's actual hours
 * (splitMode="proportional"). Pass splitMode="fixed" to restore the old static
 * 50/50 classes.
 */
export function TimesheetDayCell({
  day,
  readonly      = false,
  selected      = false,
  isUnloggedHighlight = false,
  onSelect,
  onQuickLog,
  // hour split
  splitMode  = 'proportional',
  splitAngle = '135deg',
  // quick-log selection
  selectable = false,
  onToggleSelect,
  // approver day review
  reviewMode = false,
  onApproveDay,
  onRejectDay,
}) {
  const st        = numberValue(day?.workingHours?.ST);
  const ot        = numberValue(day?.workingHours?.OT);
  const cl        = numberValue(day?.leave?.CL);
  const sl        = numberValue(day?.leave?.SL);
  const totalLeave = cl + sl;
  const status     = day?.status;
  const isApproved = status === 'approved';
  const isRejected = status === 'rejected';

  // An approved day is locked for the resource (the server enforces this too), but the
  // approver must still be able to interact with it.
  const locked      = isApproved && !reviewMode;
  const canQuickLog = !readonly && !selectable && !day?.isFuture && !day?.isHoliday && !day?.isWeekOff && !day?.isLogged;

  const gradient = splitMode === 'proportional'
    ? buildDayGradient(day, { angle: splitAngle })
    : null;

  const handleQuickLog = (e) => {
    e.stopPropagation();
    onQuickLog?.(day?.date);
  };

  const handleClick = () => {
    // While selecting for quick log, a click toggles the day instead of opening the
    // log modal.
    if (selectable) {
      onToggleSelect?.(day?.date);
      return;
    }
    onSelect?.(day);
  };

  // In review mode a logged day is clickable (opens the day's review panel) even
  // though the cell is readonly for editing — the approver still needs to select it.
  const reviewClickable = reviewMode && day?.isLogged;

  const cell = (
    <button
      type="button"
      className={getDayClass(day, { isUnloggedHighlight, selected, selectable, reviewMode })}
      style={gradient ? { background: gradient } : undefined}
      disabled={(readonly && !reviewClickable) || day?.isFuture || (locked && !reviewMode)}
      onClick={handleClick}
    >
      {/* top row: date number + day abbr + quick-log clock */}
      <span className="timesheet-day-cell__top">
        <span className="timesheet-day-cell__date">
          {day?.date ? dayjs(day.date).date() : '-'}
          {isRejected && <span className="timesheet-day-cell__reject-dot" />}
        </span>
        <span className="timesheet-day-cell__top-right">
          {canQuickLog && (
            <Tooltip title="Quick log 8h" placement="top">
              <ClockCircleOutlined
                className="timesheet-day-cell__quick-log-icon"
                onClick={handleQuickLog}
              />
            </Tooltip>
          )}
          {!canQuickLog && !day?.isHoliday && !day?.isWeekOff && !day?.isFuture && (
            <ClockCircleOutlined className="timesheet-day-cell__quick-log-icon timesheet-day-cell__quick-log-icon--logged" />
          )}
        </span>
      </span>

      {/* body: hour labels */}
      <span className="timesheet-day-cell__body">
        {day?.isHoliday && <Tag color="purple" className="timesheet-type-tag">H</Tag>}
        {day?.isWeekOff && !day?.isLogged && <Text type="secondary" className="timesheet-no-log">Off</Text>}

        {/* Standard */}
        {st > 0 && (
          <span className="timesheet-hour-label">
            <span className="timesheet-hour-value">{formatHoursShort(st)}</span>
            <span className="timesheet-hour-suffix timesheet-hour-st"> s</span>
          </span>
        )}
        {/* Overtime */}
        {ot > 0 && (
          <span className="timesheet-hour-label timesheet-hour-label--ot">
            <span className="timesheet-hour-value">{formatHoursShort(ot)}</span>
            <span className="timesheet-hour-suffix timesheet-hour-ot"> o</span>
          </span>
        )}
        {/* Leave */}
        {totalLeave > 0 && (
          <span className="timesheet-hour-label timesheet-hour-label--leave">
            <span className="timesheet-hour-value">{formatHoursShort(totalLeave)}</span>
            <span className="timesheet-hour-suffix timesheet-hour-leave"> L</span>
          </span>
        )}

        {!day?.isLogged && !day?.isHoliday && !day?.isWeekOff && !day?.isFuture && (
          <Text type="secondary" className="timesheet-no-log">No log</Text>
        )}
      </span>

      {/* Review verdict badge */}
      {isApproved && <CheckOutlined className="timesheet-day-cell__verdict timesheet-day-cell__verdict--approved" />}
      {isRejected && <CloseCircleOutlined className="timesheet-day-cell__verdict timesheet-day-cell__verdict--rejected" />}

      {/* Approver ✓/✗, revealed on hover. Rendered as spans (not <button>) because
          this cell is itself a <button> and nesting buttons is invalid HTML. */}
      {reviewMode && day?.isLogged && (onApproveDay || onRejectDay) && (
        <span className="timesheet-day-review-actions">
          {onApproveDay && (
            <span
              role="button"
              tabIndex={-1}
              className="ts-approve"
              title="Approve this day"
              onClick={(e) => { e.stopPropagation(); onApproveDay(day?.date); }}
            >
              <CheckOutlined />
            </span>
          )}
          {onRejectDay && (
            <span
              role="button"
              tabIndex={-1}
              className="ts-reject"
              title="Reject this day"
              onClick={(e) => { e.stopPropagation(); onRejectDay(day?.date); }}
            >
              <CloseCircleOutlined />
            </span>
          )}
        </span>
      )}
    </button>
  );

  // D3: hovering a rejected day surfaces WHY it was rejected. The tooltip wraps the
  // whole cell (it used to hang off a 6px dot, which was almost impossible to hit).
  if (isRejected && day?.rejectionReason) {
    return (
      <Tooltip title={day.rejectionReason} placement="top">
        {/* span wrapper: Tooltip needs a hoverable node even when the button is disabled */}
        <span style={{ display: 'block', width: '100%' }}>{cell}</span>
      </Tooltip>
    );
  }
  return cell;
}


// A standard working day is 8 hrs: Standard + Leave must always fill it.
// Overtime is separate and only capped below a full day.
const REQUIRED_DAY_HOURS = 8;
const MAX_OT_HOURS = 24;

// ── DayLogModal ────────────────────────────────────────────────────────────
export function DayLogModal({ open, day, saving = false, onCancel, onSubmit, onDelete }) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) return;
    const isWorkingDay = !day?.isWeekOff && !day?.isHoliday;
    // Fresh working days default to a full 8h standard so the common case is
    // valid out of the box; existing logs keep their saved values.
    const st = day?.isLogged
      ? numberValue(day?.workingHours?.ST)
      : (isWorkingDay ? REQUIRED_DAY_HOURS : 0);
    const ot  = numberValue(day?.workingHours?.OT);
    const cl  = numberValue(day?.leave?.CL);
    const sl  = numberValue(day?.leave?.SL);
    form.setFieldsValue({
      date:  day?.date ? dayjs(day.date) : null,
      ST:    st,
      OT:    ot,
      Leave: cl + sl,
    });
  }, [day, form, open]);

  // Standard + Leave cannot exceed 8 hrs (attached to both fields so the error
  // surfaces regardless of which one the user is editing).
  const dayHoursValidator = ({ getFieldValue }) => ({
    validator() {
      const total = numberValue(getFieldValue('ST')) + numberValue(getFieldValue('Leave'));
      return total <= REQUIRED_DAY_HOURS
        ? Promise.resolve()
        : Promise.reject(new Error(`Standard + Leave cannot exceed ${REQUIRED_DAY_HOURS} hrs`));
    },
  });

  const submit = async () => {
    const values = await form.validateFields();
    const leaveHours = numberValue(values.Leave);
    onSubmit?.({
      date:         values.date?.format('YYYY-MM-DD') ?? day?.date,
      logType:      leaveHours > 0 && numberValue(values.ST) > 0 ? 'mixed'
                    : leaveHours > 0 ? 'leave' : 'working',
      workingHours: { ST: numberValue(values.ST), OT: numberValue(values.OT) },
      leave:        { CL: leaveHours, SL: 0 },
      isHoliday:    Boolean(day?.isHoliday),
      isWeekOff:    Boolean(day?.isWeekOff),
      notes:        '',
    });
  };

  const isLocked = day?.isHoliday || day?.isWeekOff;
  const dateLabel = day?.date ? dayjs(day.date).format('MMM DD, YYYY') : 'Log time';

  return (
    <Modal
      title={
        <Space>
          <Text strong>{dateLabel}</Text>
          {day?.isHoliday && <Tag color="purple">Holiday</Tag>}
          {day?.isWeekOff  && <Tag>Week Off</Tag>}
        </Space>
      }
      open={open}
      onCancel={onCancel}
      onOk={submit}
      confirmLoading={saving}
      okText="Save"
      cancelText="Cancel"
      destroyOnHidden
      width={340}
      footer={(_, { OkBtn, CancelBtn }) => (
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            {day?.isLogged && onDelete && (
              <Button danger icon={<DeleteOutlined />} onClick={() => onDelete(day)} size="small">
                Delete
              </Button>
            )}
          </div>
          <Space>
            <CancelBtn />
            {!isLocked && <OkBtn />}
          </Space>
        </Space>
      )}
    >
      {day?.rejectionReason && (
        <Alert
          type="warning"
          showIcon
          message="Rejection note"
          description={day.rejectionReason}
          style={{ marginBottom: 12 }}
        />
      )}
      {isLocked ? (
        <Alert
          type="info"
          showIcon
          message={day?.isHoliday ? 'Holiday — no manual log required.' : 'Week off — no log required.'}
        />
      ) : (
        <Form
          form={form}
          layout="vertical"
          className="timesheet-log-form"
          onValuesChange={(changed) => {
            // Re-run the paired validator live when either side of the 8h split changes.
            if ('ST' in changed || 'Leave' in changed) {
              form.validateFields(['ST', 'Leave']).catch(() => {});
            }
          }}
        >
          <Form.Item name="date" label="Date">
            <DatePicker disabled style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="ST"
            label={<span style={{ color: '#1d4ed8', fontWeight: 600 }}>Standard <span style={{ fontWeight: 400, color: '#6b7280' }}>hrs</span></span>}
            dependencies={['Leave']}
            rules={[{ type: 'number', min: 0, max: 24 }, dayHoursValidator]}
          >
            <InputNumber min={0} max={24} step={0.5} style={{ width: '100%' }}
              addonAfter={<span style={{ color: '#1d4ed8', fontWeight: 700 }}>s</span>}
            />
          </Form.Item>
          <Form.Item
            name="OT"
            label={<span style={{ color: '#15803d', fontWeight: 600 }}>Overtime <span style={{ fontWeight: 400, color: '#6b7280' }}>hrs</span></span>}
            rules={[
              { type: 'number', min: 0, max: MAX_OT_HOURS },
              { validator: (_, v) => numberValue(v) < MAX_OT_HOURS
                  ? Promise.resolve()
                  : Promise.reject(new Error(`Overtime must be less than ${MAX_OT_HOURS} hrs`)) },
            ]}
          >
            <InputNumber min={0} max={MAX_OT_HOURS} step={0.5} style={{ width: '100%' }}
              addonAfter={<span style={{ color: '#15803d', fontWeight: 700 }}>o</span>}
            />
          </Form.Item>
          <Form.Item
            name="Leave"
            label={<span style={{ color: '#b45309', fontWeight: 600 }}>Leave <span style={{ fontWeight: 400, color: '#6b7280' }}>hrs</span></span>}
            dependencies={['ST']}
            rules={[{ type: 'number', min: 0, max: 24 }, dayHoursValidator]}
          >
            <InputNumber min={0} max={24} step={0.5} style={{ width: '100%' }}
              addonAfter={<span style={{ color: '#b45309', fontWeight: 700 }}>L</span>}
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}

// ── QuickLogSelectionBar ───────────────────────────────────────────────────
/**
 * Footer bar for quick-log selection.
 *
 * Quick Log does not submit blind and does not open a modal: it strokes every eligible
 * day in the calendar, the user clicks cells to deselect any they don't want, and this
 * bar confirms only what is still selected.
 */
export function QuickLogSelectionBar({
  count = 0,
  total = 0,
  hours = 8,
  loading = false,
  onConfirm,
  onCancel,
  onSelectAll,
  onSelectNone,
}) {
  return (
    <div className="timesheet-quicklog-bar">
      <Space size={8} wrap>
        <Text strong>
          {count === 0
            ? 'No days selected'
            : `Log ${hours}h standard on ${count} day${count === 1 ? '' : 's'}`}
        </Text>
        {total > 0 && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {count} of {total} eligible · click a day to deselect it
          </Text>
        )}
      </Space>
      <Space size={6}>
        <Button size="small" onClick={onSelectAll} disabled={count === total}>Select all</Button>
        <Button size="small" onClick={onSelectNone} disabled={count === 0}>Clear</Button>
        <Button size="small" onClick={onCancel}>Cancel</Button>
        <Button
          size="small"
          type="primary"
          icon={<ClockCircleOutlined />}
          loading={loading}
          disabled={count === 0}
          onClick={onConfirm}
        >
          Confirm
        </Button>
      </Space>
    </div>
  );
}

// ── RejectionReasonModal ───────────────────────────────────────────────────
export function RejectionReasonModal({ open = false, loading = false, onSave, onCancel }) {
  const [reason, setReason] = useState('');

  useEffect(() => { if (open) setReason(''); }, [open]);

  return (
    <Modal
      title="Rejection Reason"
      open={open}
      onCancel={onCancel}
      footer={
        <Space>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" danger disabled={!reason.trim()} loading={loading} onClick={() => onSave?.(reason.trim())}>
            Reject
          </Button>
        </Space>
      }
      destroyOnHidden
    >
      <Form layout="vertical">
        <Form.Item
          label={<span><span style={{ color: '#ff4d4f' }}>*</span> Comments</span>}
          help={!reason.trim() ? 'Rejection reason is required' : ''}
          validateStatus={!reason.trim() ? 'error' : ''}
        >
          <Input.TextArea
            rows={4} maxLength={1000} showCount
            placeholder="Enter reason for rejection..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── QuickLogBar (kept for backward compat) ─────────────────────────────────
export function QuickLogBar({ loading = false, disabled = false, onQuickLog }) {
  return (
    <Card size="small" className="timesheet-quick-log">
      <Space wrap>
        <AppButton
          variant="primary"
          icon={<ClockCircleOutlined />}
          loading={loading}
          disabled={disabled}
          onClick={() => onQuickLog?.('businessDays')}
        >
          Quick Log (8h s)
        </AppButton>
      </Space>
    </Card>
  );
}

// ── TimesheetSummaryPanel ──────────────────────────────────────────────────
export function TimesheetSummaryPanel({ summary = {}, status, monthLabel, weeks }) {
  const topItems = [
    { label: 'Unapproved Payable hours', sublabel: '(S + O)',          value: summary.unapprovedPayableHours ?? summary.payableHours },
    { label: 'Non-payable hours',        sublabel: '(Leave/Holidays)', value: summary.nonPayableHours },
    { label: 'Approved Payable hours',   sublabel: '',                 value: summary.approvedPayableHours, valueColor: '#15803d' },
  ];

  const typeBlocks = [
    { label: 'Standard',  value: summary.totalSTHours,      color: '#1d4ed8', bg: '#D0E6FF' },
    { label: 'Overtime',  value: summary.totalOTHours,      color: '#15803d', bg: '#d9f7e3' },
    { label: 'Leaves',    value: summary.leaveHours,        color: '#b45309', bg: '#FFECBA' },
    { label: 'Holidays',  value: summary.holidayHours ?? 0, color: '#6d28d9', bg: '#ede9fe' },
  ];

  const hasWeeks = Array.isArray(weeks) && weeks.length > 0;

  // Sidebar of independent cards (Monthly Summary / Weekly Totals / Hour bars),
  // each a compact white AppCard, matching the timesheet Figma.
  return (
    <div className="timesheet-summary">
      {/* Monthly Summary */}
      <Card className="timesheet-summary-card" size="small">
        <div className="timesheet-summary-card__head">
          <Title level={5} style={{ margin: 0 }}>Monthly Summary</Title>
          <StatusBadgeTag status={status} />
        </div>
        <div className="timesheet-summary-card__metrics">
          {topItems.map(({ label, sublabel, value, valueColor }) => (
            <div key={label} className="timesheet-summary-card__metric-row">
              <Text type="secondary" style={{ fontSize: 12 }}>
                {label} {sublabel && <span style={{ fontSize: 11 }}>{sublabel}</span>}
              </Text>
              <Text strong style={{ fontSize: 13, whiteSpace: 'nowrap', color: valueColor }}>{formatHours(value)}</Text>
            </div>
          ))}
        </div>
      </Card>

      {/* Weekly Totals */}
      {hasWeeks && (
        <Card className="timesheet-summary-card" size="small">
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>Weekly Totals (S + O)</Text>
          <div className="timesheet-summary-card__weekly">
            {weeks.map((week, i) => {
              const payable = numberValue(week?.weeklyTotals?.payable);
              const start = week.weekStart ? dayjs(week.weekStart).format('MMM D') : '';
              const end   = week.weekEnd   ? dayjs(week.weekEnd).format('MMM D')   : '';
              return (
                <div key={i} className="timesheet-summary-card__week-row">
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Week {week.weekNumber ?? i + 1}
                    {start && end ? ` (${start} - ${end})` : ''}
                  </Text>
                  <Text strong style={{ fontSize: 12 }}>{formatHours(payable)}</Text>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Hour-type bars */}
      <Card className="timesheet-summary-card" size="small">
        <div className="timesheet-summary-card__bars">
          {typeBlocks.map(({ label, value, color, bg }) => (
            <div key={label} className="timesheet-summary-card__bar" style={{ background: bg }}>
              <Text style={{ fontSize: 12, color, fontWeight: 500 }}>{label}</Text>
              <Text strong style={{ fontSize: 13, color }}>{formatHours(value)}</Text>
            </div>
          ))}
        </div>
      </Card>

      {summary.missingDays > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`${summary.missingDays} working day${summary.missingDays > 1 ? 's' : ''} not logged`}
        />
      )}
    </div>
  );
}

// ── ProofUploadPreview ─────────────────────────────────────────────────────
export function ProofUploadPreview({ proof, uploading = false, disabled = false, onUpload, onPreview }) {
  return (
    <Card className="timesheet-proof" size="small" title="Proof / screenshot">
      {proof?.fileName ? (
        <div className="timesheet-proof__file">
          <FileTextOutlined style={{ color: '#1677ff' }} />
          <Text strong style={{ flex: 1, minWidth: 0 }} ellipsis>{proof.fileName}</Text>
          {onPreview && (
            <Button type="link" size="small" onClick={() => onPreview(proof)}>Preview</Button>
          )}
        </div>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>Upload Proof</Text>
      )}
      <Upload
        beforeUpload={(file) => { onUpload?.(file); return false; }}
        maxCount={1}
        showUploadList={false}
        disabled={disabled}
        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
      >
        {!disabled && (
          <Button icon={<UploadOutlined />} loading={uploading} block size="small" style={{ marginTop: 6 }}>
            {proof?.fileName ? 'Replace proof' : 'Upload proof'}
          </Button>
        )}
      </Upload>
      <Text type="secondary" style={{ fontSize: 11 }}>
        Supports: .pdf, .doc, .docx, .png, .jpg — max 10 MB
      </Text>
    </Card>
  );
}

// ── NotesPanel ─────────────────────────────────────────────────────────────
export function NotesPanel({ value, disabled = false, onChange, label = 'Notes / Comments', maxLength = 1000 }) {
  const count = (value ?? '').length;
  return (
    <Card className="timesheet-notes" size="small" title={label}>
      <div className="timesheet-notes__field">
        <Input.TextArea
          className="timesheet-notes__textarea"
          rows={5}
          maxLength={maxLength}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder="Add notes or comments..."
        />
      </div>
      {/* Static count row (replaces antd showCount, which overlaps the card edge) */}
      <div className="timesheet-notes__count">{count} / {maxLength}</div>
    </Card>
  );
}

// ── ApprovalActionBar ──────────────────────────────────────────────────────
export function ApprovalActionBar({
  status,
  loading    = false,
  readonly   = false,
  rejectionReason,
  onSubmit,
  onApprove,
  onReject,
  onReopen,
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);

  const pendingManagerAction = ['submitted', 'resubmitted', 'save_draft'].includes(status);
  const isRejected  = status === 'rejected';
  const isApproved  = status === 'approved';
  const isSubmitted = status === 'submitted' || status === 'resubmitted';
  const isDraft     = !status || status === 'draft' || status === 'not_submitted';

  const handleReject = async (reason) => {
    setRejectLoading(true);
    try { await onReject?.(reason); setRejectOpen(false); }
    finally { setRejectLoading(false); }
  };

  // Determine submit button label + disabled state
  let submitLabel = 'Submit';
  let submitDisabled = false;
  if (isSubmitted)   { submitLabel = status === 'resubmitted' ? 'Re-Submitted' : 'Submitted'; submitDisabled = true; }
  if (isRejected)    { submitLabel = 'Re-Submit'; submitDisabled = false; }
  if (isApproved)    { submitLabel = 'Approved'; submitDisabled = true; }

  return (
    <>
      {rejectionReason && isRejected && (
        <Alert type="error" showIcon message="Rejected" description={rejectionReason} style={{ marginBottom: 8 }} />
      )}
      <div className="timesheet-action-bar">
        {/* Resource submit / re-submit — always visible, disabled when already submitted */}
        {(isDraft || isRejected || isSubmitted || isApproved) && onSubmit && (
          <AppButton
            variant="primary"
            icon={<SendOutlined />}
            loading={loading}
            disabled={submitDisabled}
            onClick={!submitDisabled ? onSubmit : undefined}
            block
          >
            {submitLabel}
          </AppButton>
        )}

        {/* Manager approve */}
        {pendingManagerAction && onApprove && (
          <AppButton variant="primary" icon={<CheckOutlined />} loading={loading} onClick={onApprove}>
            Approve
          </AppButton>
        )}
        {/* Manager reject */}
        {pendingManagerAction && onReject && (
          <Button danger loading={loading} onClick={() => setRejectOpen(true)}>Reject</Button>
        )}
        {/* Reopen */}
        {(isApproved || isRejected) && onReopen && (
          <Button
            icon={<InfoCircleOutlined />}
            loading={loading}
            onClick={() => Modal.confirm({
              title: 'Reopen timesheet?',
              content: 'The timesheet will return to submitted state.',
              onOk: onReopen,
            })}
          >
            Reopen
          </Button>
        )}
      </div>

      <RejectionReasonModal
        open={rejectOpen}
        loading={rejectLoading}
        onSave={handleReject}
        onCancel={() => setRejectOpen(false)}
      />
    </>
  );
}
